import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Select from "../../components/common/Select";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import {
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useGetAccessLevelsQuery,
} from "../../store/api";

const UserForm = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(userId);

  const { data: user } = useGetUserQuery(userId, { skip: !userId });
  const { data: accessLevels } = useGetAccessLevelsQuery();
  const [createUser, createState] = useCreateUserMutation();
  const [updateUser, updateState] = useUpdateUserMutation();

  const [form, setForm] = useState({
    displayName: "",
    pin: "",
    accessLevelId: "",
    isActive: true,
  });

  useEffect(() => {
    if (user) {
      setForm({
        displayName: user.displayName || user.name || user.fullName || "",
        pin: "", // Don't populate PIN for security
        accessLevelId: user.accessLevelId || "",
        isActive: user.isActive !== undefined ? user.isActive : true,
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Debug: Log the payload being sent
    console.log("Submitting user payload:", form);

    try {
      if (isEditing) {
        await updateUser({ userId, payload: form }).unwrap();
      } else {
        await createUser(form).unwrap();
      }
      navigate("/users");
    } catch (err) {
      console.error("Error creating/updating user:", err);
      // Error handled by UI
    }
  };

  const accessLevelsList = Array.isArray(accessLevels) ? accessLevels : [];
  const selectedAccessLevel = accessLevelsList.find(
    (level) =>
      String(level.accessLevelId || level.id || "") ===
      String(form.accessLevelId || ""),
  );
  const selectedAccessLevelScreenCount = Array.isArray(
    selectedAccessLevel?.appOptionIds,
  )
    ? selectedAccessLevel.appOptionIds.length
    : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEditing ? "Edit User" : "Add User"}
        subtitle={
          isEditing ? "Update user information" : "Create a new user account"
        }
        actions={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate("/users")}
          >
            Back to Users
          </Button>
        }
      />

      <Card>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Display Name"
              name="displayName"
              placeholder="John Doe"
              value={form.displayName}
              onChange={handleChange}
              required
            />
            <Select
              label="Access Level"
              name="accessLevelId"
              value={form.accessLevelId}
              onChange={handleChange}
            >
              <option value="">None (Optional)</option>
              {accessLevelsList.map((level) => (
                <option
                  key={level.accessLevelId || level.id}
                  value={level.accessLevelId || level.id}
                >
                  {level.name || level.displayName}
                  {Array.isArray(level.appOptionIds)
                    ? ` (${level.appOptionIds.length} screens)`
                    : ""}
                </option>
              ))}
            </Select>
          </div>

          {selectedAccessLevel && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
              <div className="font-semibold text-slate-900 dark:text-slate-100">
                Selected role summary
              </div>
              <div className="mt-1">
                {selectedAccessLevel.name || "Access Level"}
                {selectedAccessLevelScreenCount
                  ? ` · ${selectedAccessLevelScreenCount} screens`
                  : ""}
              </div>
            </div>
          )}

          <Input
            label={isEditing ? "PIN (leave blank to keep current)" : "PIN"}
            name="pin"
            type="password"
            placeholder="Enter 4-digit PIN"
            value={form.pin}
            onChange={handleChange}
            required={!isEditing}
            helper="4-digit PIN for login"
            maxLength="4"
            pattern="[0-9]{4}"
          />

          <Checkbox
            label="Active"
            name="isActive"
            checked={form.isActive}
            onChange={handleChange}
          />

          {(createState.isSuccess || updateState.isSuccess) && (
            <Alert tone="success">
              User {isEditing ? "updated" : "created"} successfully.
            </Alert>
          )}
          {(createState.isError || updateState.isError) && (
            <Alert tone="error">
              {createState.error?.data?.detail ||
                updateState.error?.data?.detail ||
                "Failed to save user."}
            </Alert>
          )}

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={createState.isLoading || updateState.isLoading}
            >
              {isEditing ? "Update" : "Create"} User
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate("/users")}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default UserForm;
